import { Dialog } from 'primereact/dialog';
import { GdprSummaryResponse } from '@/api/gdpr';
import { Button, CloseIcon, CopyIcon, Toast } from '@repo/atomic-components';
import { FC, ReactNode, useRef } from 'react';
import { copyToClipboard } from '@repo/utils';

interface GdprCheckSummaryModalProps {
  visible: boolean;
  onHide: () => void;
  summaryData: GdprSummaryResponse | null;
  isLoading?: boolean;
}

/**
 * DialogShell
 * Shared chrome for all three states (loading, no-data, summary). Mirrors the
 * GdprSummaryModal design on the GDPR page — fixed 500px max width,
 * 8px corner radius, fletchers-gray-110 1px border, custom header (PrimeReact's
 * default header is hidden), 24px / 16px content padding.
 */
const DialogShell: FC<{
  visible: boolean;
  onHide: () => void;
  title: string;
  body: ReactNode;
  footer?: ReactNode;
}> = ({ visible, onHide, title, body, footer }) => (
  <Dialog
    visible={visible}
    onHide={onHide}
    style={{
      width: '90vw',
      maxWidth: '500px',
      borderRadius: '8px',
      border: '1px solid #e1e1e0',
      overflow: 'hidden',
    }}
    contentClassName="!p-0"
    headerClassName="!hidden"
  >
    <div className="border-fletchers-gray-110 flex items-center justify-between border-b px-[24px] py-[16px]">
      <div className="text-fletchers-black-900 text-[18px]/[18px] font-[600]">{title}</div>
      <button
        type="button"
        aria-label="Close"
        onClick={onHide}
        className="cursor-pointer rounded p-1 focus:outline-none"
      >
        <CloseIcon width={12} height={12} />
      </button>
    </div>

    <div className="flex flex-col gap-[16px] px-[24px] py-[16px]">{body}</div>

    {footer && <div className="flex items-center gap-2 px-[24px] pb-[24px]">{footer}</div>}
  </Dialog>
);

export const GdprCheckSummaryModal = ({
  visible,
  onHide,
  summaryData,
  isLoading = false,
}: GdprCheckSummaryModalProps) => {
  const toast = useRef<Toast>(null);

  const handleClose = () => {
    onHide();
  };

  const handleCopy = async () => {
    if (!summaryData) return;
    const breachPageNumbers =
      summaryData.breachPages && summaryData.breachPages.length > 0
        ? summaryData.breachPages.map((p) => p.pageNumber).join(', ')
        : 'None';
    // Plain-text version of every line shown in the modal body, in display order.
    const text = [
      `Total pages in document: ${summaryData.pageCount}`,
      `AI-supported check: ${summaryData.aiChecked ? 'True' : 'False'}`,
      `Total pages flagged by AI: ${summaryData.flagCount}`,
      `Total pages with Breach label: ${summaryData.breachCount}`,
      `Page numbers of breaches: ${breachPageNumbers}`,
      `Total pages with Blank label: ${summaryData.blankCount}`,
    ].join('\n');
    await copyToClipboard(text);
    toast.current?.show({
      severity: 'success',
      summary: 'Copied',
      detail: 'Summary copied to clipboard.',
      life: 2000,
    });
  };

  if (isLoading) {
    return (
      <>
        <Toast ref={toast} />
        <DialogShell
          visible={visible}
          onHide={handleClose}
          title="GDPR check summary"
          body={<div className="text-fletchers-gray-175 text-[14px]">Loading summary…</div>}
        />
      </>
    );
  }

  if (!summaryData) {
    return (
      <>
        <Toast ref={toast} />
        <DialogShell
          visible={visible}
          onHide={handleClose}
          title="GDPR check summary"
          body={<div className="text-fletchers-gray-175 text-[14px]">No summary data available.</div>}
        />
      </>
    );
  }

  const { breachPages, breachCount, blankCount, flagCount, pageCount, aiChecked } = summaryData;

  return (
    <>
      <Toast ref={toast} />
      <DialogShell
        visible={visible}
        onHide={handleClose}
        title="GDPR check summary"
        body={
          <>
            <div className="text-[14px] text-black">Total pages in document: {pageCount}</div>

            <div className="text-[14px] text-black">
              <span>AI-supported check: </span>
              <span className="text-fletchers-green-500 font-semibold">{aiChecked ? 'True' : 'False'}</span>
            </div>

            <div className="text-[14px] text-black">Total pages flagged by AI: {flagCount}</div>
            <div className="text-[14px] text-black">Total pages with Breach label: {breachCount}</div>

            <div className="text-[14px] text-black">
              <span>Page numbers of breaches: </span>
              {breachPages && breachPages.length > 0 ? (
                breachPages.map((page, index) => (
                  <span key={page.pageId}>
                    <span className="text-fletchers-green-500 font-semibold">{page.pageNumber}</span>
                    {index < breachPages.length - 1 && ', '}
                  </span>
                ))
              ) : (
                <span className="text-fletchers-green-500 font-semibold">None</span>
              )}
            </div>

            <div className="text-[14px] text-black">Total pages with Blank label: {blankCount}</div>
          </>
        }
        footer={
          <Button
            outlined
            className="!h-[32px] hover:!bg-gray-50"
            icon={<CopyIcon width={14} height={14} />}
            onClick={handleCopy}
          >
            <span className="ml-2">Copy</span>
          </Button>
        }
      />
    </>
  );
};
